import { GA_ENHANCED_ECOMMERCE } from '../../constants'
const CALLBACK_TIMEOUT = 500

/**
 * Google Analytics tracker
 */
class GATracker {
  /**
   * Constructor
   * @param {ReactGA} reactGA
   * @param {Object} trackerSettings
   * @param {string} trackerSettings.name Name of the tracker object
   */
  constructor (reactGA, trackerSettings = {}) {
    const {
      trackingId,
      gaOptions: {
        name
      }
    } = trackerSettings

    this.reactGA = reactGA
    this.trackingId = trackingId
    this.name = name
  }

  /**
   * send pageview event to according tracker
   *
   * @param {string} path
   */
  pageview (path) {
    this.reactGA.pageview(path, [ this.name ])
  }

  /**
   * set wrapper for according tracker
   *
   * @param {Object} fieldObject
   */
  set (fieldObject) {
    this.reactGA.set(fieldObject, [ this.name ])
  }

  /**
   * load ga module for according tracker
   * @param {string} pluginName
   * @param {object} pluginOptions
   */
  require (pluginName, pluginOptions = null) {
    this.reactGA.ga(`${this.name}.require`, pluginName, pluginOptions)
  }

  /**
   * handle ecommerce event
   *
   * @param action
   * @param payload
   */
  ecommerce (action, payload) {
    const mappedPayload = Array.isArray(payload) ? payload : [ payload ]

    this.reactGA.ga(`${this.name}.${GA_ENHANCED_ECOMMERCE.ECOMMERCE_PLUGIN}:${action}`, ...mappedPayload)
  }

  /**
   * add impression
   *
   * @param {object} impressionFieldObject
   * @param {string} impressionFieldObject.id
   * @param {string} impressionFieldObject.name
   * @param {string} impressionFieldObject.category
   * @param {string} impressionFieldObject.list
   * @param {number} impressionFieldObject.position
   */
  addImpression (impressionFieldObject) {
    this.ecommerce('addImpression', impressionFieldObject)
  }

  /**
   * add product
   *
   * @param {object} productFieldObject
   * @param {string} productFieldObject.id
   * @param {string} productFieldObject.name
   * @param {string} productFieldObject.category
   * @param {string} productFieldObject.price
   * @param {number} productFieldObject.quantity
   */
  addProduct (productFieldObject) {
    this.ecommerce('addProduct', productFieldObject)
  }

  /**
   * purchase
   *
   * @param {object} actionFieldObject
   * @param {string} actionFieldObject.id
   * @param {string} actionFieldObject.revenue
   */
  purchase (actionFieldObject) {
    this.ecommerce('setAction', actionFieldObject)
  }

  /**
   * fire ga event
   *
   * @param {object} eventArgs
   * @param {string} eventArgs.category
   * @param {string} eventArgs.action
   * @param {bool} eventArgs.nonInteraction
   */
  event (eventArgs) {
    this.reactGA.event(eventArgs, [ this.name ])
  }

  /**
   * handle purchase event
   * @param {object} purchaseFieldObject
   * @param {string} purchaseFieldObject.id
   * @param {string} purchaseFieldObject.name
   * @param {string} purchaseFieldObject.category
   * @return {Promise<any>}
   */
  handlePurchaseEvent (purchaseFieldObject) {
    return new Promise((resolve, reject) => {
      const timeoutCallback = setTimeout(reject, CALLBACK_TIMEOUT)
      const productFieldObject = Object.assign({}, purchaseFieldObject, GA_ENHANCED_ECOMMERCE.PURCHASE_PAYLOAD)
      this.addProduct(productFieldObject)

      const actionFieldObject = [
        'purchase',
        {
          id: purchaseFieldObject.id,
          revenue: GA_ENHANCED_ECOMMERCE.PURCHASE_PAYLOAD.price
        }
      ]
      this.purchase(actionFieldObject)

      this.reactGA.ga(
        `${this.name}.send`,
        'event',
        GA_ENHANCED_ECOMMERCE.ECOMMERCE_CATEGORY,
        'Click',
        {
          hitCallback: () => {
            clearTimeout(timeoutCallback)
            return resolve()
          }
        }
      )
    })
  }
}

export default GATracker
